import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  NavBar, 
  Card, 
  Button, 
  Form, 
  Input, 
  List,
  Space,
  Toast,
  Dialog,
  Switch,
  Tag
} from 'antd-mobile';
import { 
  LeftOutline, 
  LockOutline, 
  EyeInvisibleOutline,
  EyeOutline,
  ClockCircleOutline,
  EnvironmentOutline,
  SetOutline,
  KeyOutline,
  CheckCircleOutline,
  CloseCircleOutline
} from 'antd-mobile-icons';
import { useI18n } from '@/hooks/useI18n';
import { getUserInfo, type User } from '@/lib/api/user';
import { isLoggedIn } from '@/lib/auth';

export default function AccountSecurity() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm] = Form.useForm();
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  // 模拟登录记录数据
  const loginHistory = [
    {
      id: '1',
      time: '2024-01-15 14:30:25',
      location: '北京市朝阳区',
      device: 'Chrome 浏览器',
      ip: '192.168.1.100',
      status: 'success'
    },
    {
      id: '2',
      time: '2024-01-14 09:15:42',
      location: '北京市朝阳区',
      device: 'Safari 浏览器',
      ip: '192.168.1.101',
      status: 'success'
    },
    {
      id: '3',
      time: '2024-01-13 16:22:18',
      location: '上海市浦东新区',
      device: 'Chrome 浏览器',
      ip: '10.0.0.50',
      status: 'failed'
    }
  ];

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/login');
      return;
    }
    fetchUserInfo();
  }, [navigate]);

  const fetchUserInfo = async () => {
    try {
      const response = await getUserInfo();
      if (response.success && response.data) {
        setUser(response.data);
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      setChangingPassword(true);
      const values = await passwordForm.validateFields();
      
      // 这里应该调用实际的密码修改API
      console.log('修改密码:', values);
      
      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      Toast.show({
        icon: 'success',
        content: '密码修改成功'
      });
      
      setShowChangePassword(false);
      passwordForm.resetFields();
    } catch (error) {
      console.error('修改密码失败:', error);
      Toast.show({
        icon: 'fail',
        content: '密码修改失败，请重试'
      });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleTwoFactorToggle = (checked: boolean) => {
    Dialog.confirm({
      content: checked ? '确定要启用双因素认证吗？' : '确定要关闭双因素认证吗？',
      onConfirm: () => {
        setTwoFactorEnabled(checked);
        Toast.show({
          icon: 'success',
          content: checked ? '双因素认证已启用' : '双因素认证已关闭'
        });
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
          <p className="text-gray-500">{t('common.loading')}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex justify-center">
      <div className="w-full min-h-screen relative overflow-hidden" style={{ maxWidth: '480px' }}>
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-pink-400/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-cyan-400/10 to-blue-400/10 rounded-full blur-3xl" />
      </div>

      {/* 顶部导航 */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <NavBar 
          onBack={() => navigate('/profile')}
          className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50"
        >
          <div className="flex items-center text-gray-800">
            <SetOutline className="mr-2" />
            账户安全
          </div>
        </NavBar>
      </motion.div>

      <div className="p-4 relative z-10">
        {/* 安全概览 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-green-500 to-emerald-500 rounded-full mr-3" />
                安全概览
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <motion.div 
                  whileHover={{ scale: 1.02 }}
                  className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100"
                >
                  <div className="flex items-center justify-between mb-2">
                    <CheckCircleOutline className="text-green-500 text-xl" />
                    <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">安全</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">账户状态</p>
                  <p className="text-lg font-semibold text-gray-800">正常</p>
                </motion.div>
                
                <motion.div 
                  whileHover={{ scale: 1.02 }}
                  className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100"
                >
                  <div className="flex items-center justify-between mb-2">
                    <LockOutline className="text-blue-500 text-xl" />
                    <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">已设置</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">登录密码</p>
                  <p className="text-lg font-semibold text-gray-800">已设置</p>
                </motion.div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* 密码管理 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mt-4"
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full mr-3" />
                密码管理
              </h3>
              
              <motion.div
                whileHover={{ x: 4 }}
                className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50/50 to-gray-100/50 rounded-xl cursor-pointer"
                onClick={() => setShowChangePassword(true)}
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mr-3">
                    <KeyOutline className="text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">修改登录密码</p>
                    <p className="text-sm text-gray-500">定期更换密码，保护账户安全</p>
                  </div>
                </div>
                <div className="text-gray-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </motion.div>
            </div>
          </Card>
        </motion.div>

        {/* 双因素认证 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mt-4"
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full mr-3" />
                双因素认证
              </h3>
              
              <motion.div
                whileHover={{ x: 4 }}
                className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50/50 to-gray-100/50 rounded-xl"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-3">
                    <SetOutline className="text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">双因素认证</p>
                    <p className="text-sm text-gray-500">为账户添加额外的安全保护</p>
                  </div>
                </div>
                <Switch 
                  checked={twoFactorEnabled}
                  onChange={handleTwoFactorToggle}
                  className="ml-4"
                />
              </motion.div>
            </div>
          </Card>
        </motion.div>

        {/* 登录记录 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="mt-4"
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-orange-500 to-red-500 rounded-full mr-3" />
                登录记录
              </h3>
              
              <div className="space-y-3">
                {loginHistory.map((record, index) => (
                  <motion.div
                    key={record.id}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    whileHover={{ x: 4 }}
                    className="p-4 bg-gradient-to-r from-gray-50/50 to-gray-100/50 rounded-xl"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-3 ${
                          record.status === 'success' ? 'bg-green-400' : 'bg-red-400'
                        }`} />
                        <div>
                          <div className="flex items-center space-x-2 mb-1">
                            <ClockCircleOutline className="text-gray-400 text-sm" />
                            <span className="text-sm font-medium text-gray-800">{record.time}</span>
                          </div>
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <div className="flex items-center">
                              <EnvironmentOutline className="mr-1" />
                              {record.location}
                            </div>
                            <span>{record.device}</span>
                            <span>{record.ip}</span>
                          </div>
                        </div>
                      </div>
                      <Tag 
                        color={record.status === 'success' ? 'success' : 'danger'}
                        className="text-xs"
                      >
                        {record.status === 'success' ? '成功' : '失败'}
                      </Tag>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </Card>
        </motion.div>

        {/* 底部间距 */}
        <div className="h-8" />
      </div>

      {/* 修改密码弹窗 */}
      <AnimatePresence>
        {showChangePassword && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowChangePassword(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-6 text-center">修改密码</h3>
              
              <Form form={passwordForm} layout="vertical">
                <Form.Item
                  name="currentPassword"
                  label="当前密码"
                  rules={[{ required: true, message: '请输入当前密码' }]}
                >
                  <div className="relative">
                    <Input
                      type={showCurrentPassword ? 'text' : 'password'}
                      placeholder="请输入当前密码"
                      className="pr-10 rounded-xl"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? <EyeOutline /> : <EyeInvisibleOutline />}
                    </button>
                  </div>
                </Form.Item>
                
                <Form.Item
                  name="newPassword"
                  label="新密码"
                  rules={[
                    { required: true, message: '请输入新密码' },
                    { min: 6, message: '密码长度至少6位' }
                  ]}
                >
                  <div className="relative">
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="请输入新密码"
                      className="pr-10 rounded-xl"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOutline /> : <EyeInvisibleOutline />}
                    </button>
                  </div>
                </Form.Item>
                
                <Form.Item
                  name="confirmPassword"
                  label="确认新密码"
                  dependencies={['newPassword']}
                  rules={[
                    { required: true, message: '请确认新密码' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('newPassword') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('两次输入的密码不一致'));
                      },
                    }),
                  ]}
                >
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="请再次输入新密码"
                      className="pr-10 rounded-xl"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOutline /> : <EyeInvisibleOutline />}
                    </button>
                  </div>
                </Form.Item>
              </Form>
              
              <div className="flex space-x-3 mt-6">
                <Button
                  block
                  onClick={() => setShowChangePassword(false)}
                  className="bg-gray-100 text-gray-600 border-0 rounded-xl"
                >
                  取消
                </Button>
                <Button
                  block
                  color="primary"
                  onClick={handleChangePassword}
                  loading={changingPassword}
                  className="bg-gradient-to-r from-blue-500 to-indigo-500 border-0 rounded-xl"
                >
                  确认修改
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
}